import {Helmet} from '@dr.pogodin/react-helmet';
import {
  openDeleteDialog,
  openDialog,
  showNotification,
  DataStatus,
  Table,
  TableRow,
  TableRowAction,
  TableRowActionsData,
} from 'platform/components';
import {Align, Text} from 'platform/foundation';

import {indexBy, isEmpty, map, prop} from 'ramda';

import {useGetBranchListQuery} from '@dms/api/core';
import {
  DocumentSeriesDefinitionResponseBody,
  useDeleteDocumentSeriesDefinitionMutation,
  useGetDocumentSeriesDefinitionListQuery,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader, useSeriesOptions} from '@dms/settings/shared';
import {EMPTY_PLACEHOLDER, handleApiError} from '@dms/shared';

import {convertStringToCamelCase} from 'shared/web';

import {InvoiceDefinitionForm} from './components/InvoiceDefinitionForm';

export function InvoiceDefinition() {
  const [deleteDefinition] = useDeleteDocumentSeriesDefinitionMutation();

  const {groupedSeries, isSeriesError, isSeriesLoading} = useSeriesOptions(['accounting/invoice']);

  const {data: branchList} = useGetBranchListQuery();
  const {data: invoiceDefinitionItems} = useGetDocumentSeriesDefinitionListQuery();

  const branchMap = map(
    prop('marketingName'),
    indexBy(prop('id'), branchList?.branchListItems ?? [])
  );

  const seriesMap = map(
    prop('label'),
    indexBy(prop('value'), groupedSeries['accounting/invoice'] ?? [])
  );

  const title = i18n.t('page.accounting.labels.invoiceDefinition');

  const handleRowClick = (item: DocumentSeriesDefinitionResponseBody) => {
    openDialog(
      <InvoiceDefinitionForm
        definition={item}
        data-testid={testIds.settings.invoiceDefinition('InvoiceDefinitionForm')}
      />,
      {
        title,
        scrollBehavior: 'outside',
      }
    );
  };

  const renderRow = (item: DocumentSeriesDefinitionResponseBody, index: number) => {
    const branchName = branchMap[item?.branchId || ''];
    const seriesName = seriesMap[item?.documentSeriesId];

    const actions: TableRowActionsData = {
      primary: [
        {
          title: i18n.t('general.labels.edit'),
          icon: 'image/edit' as const,
          onClick: () =>
            openDialog(
              <InvoiceDefinitionForm
                definition={item}
                data-testid={testIds.settings.invoiceDefinition('InvoiceDefinitionForm')}
              />,
              {
                title: i18n.t('page.accounting.labels.invoiceDefinition'),
                scrollBehavior: 'outside',
              }
            ),
        },
        ...(item?.isSystem
          ? []
          : [
              {
                title: i18n.t('general.labels.delete'),
                icon: 'action/delete' as const,
                onClick: () => {
                  if (!item?.isSystem && item?.id) {
                    openDeleteDialog({
                      text: i18n.t('entity.accounting.labels.deleteDefinitionConfirmationText', {
                        name: item?.name ?? '',
                      }),
                      onConfirm: () =>
                        deleteDefinition({documentSeriesDefinitionId: item.id})
                          .unwrap()
                          .then(showNotification.success)
                          .catch(handleApiError),
                    });
                  }
                },
              } as TableRowAction,
            ]),
      ],
    };

    return (
      <TableRow
        key={item.id}
        onRowClick={() => handleRowClick(item)}
        actions={actions}
        data-testid={testIds.settings.invoiceDefinition(`tableRow-${index}`)}
      >
        <Text alternative size="small">
          {item?.name ?? EMPTY_PLACEHOLDER}
        </Text>
        <Text size="small">{branchName ?? EMPTY_PLACEHOLDER}</Text>
        <Text size="small">{seriesName ?? EMPTY_PLACEHOLDER}</Text>
        <Text size="small">
          {i18n.t(
            `entity.checkout.labels.${convertStringToCamelCase(item?.invoicingDocumentType)}`
          )}
        </Text>
      </TableRow>
    );
  };

  const columns = [
    {element: <Align left>{i18n.t('entity.invoice.labels.name')}</Align>},
    {element: <Align left>{i18n.t('entity.branch.labels.branch')}</Align>},
    {element: <Align left>{i18n.t('entity.document.labels.seriesTitle')}</Align>},
    {element: <Align left>{i18n.t('entity.document.labels.invoiceType')}</Align>},
  ];

  const handleCreateDefinition = () =>
    openDialog(
      <InvoiceDefinitionForm
        definition={null}
        data-testid={testIds.settings.invoiceDefinition('InvoiceDefinitionForm')}
      />,
      {
        title,
        scrollBehavior: 'outside',
      }
    );

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accounting.actions.newInvoiceDefinition'),
        onClick: handleCreateDefinition,
        'data-testid': testIds.settings.invoiceDefinition('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={header}
        data-testid="settings-invoice-definition-modal"
        isLoading={isSeriesLoading}
        isError={isSeriesError}
      >
        <DataStatus isEmpty={isEmpty(invoiceDefinitionItems)}>
          <Table data-testid={testIds.settings.invoiceDefinition('table')} columns={columns}>
            {(invoiceDefinitionItems || []).map(renderRow)}
          </Table>
        </DataStatus>
      </SettingsTemplate>
    </>
  );
}
